import { simplifiedDriveService } from './googleDriveSimplified';
import { dataBackupService } from './dataBackupService';

const LAST_SYNC_KEY = 'godmode_last_sync_time';
const SYNC_DEBOUNCE_MS = 30_000;
const DATA_HASH_KEY = 'godmode_data_hash';

// Keys to watch for changes that should trigger a sync
const WATCHED_KEYS = [
  'godmode_aspirations',
  'godmode_goals',
  'godmode_lists',
  'godmode_list_schedules',
  'godmode_universal_completions',
  'godmode_user_profile',
  'godmode_ai_config',
];

export type CloudSyncStatus = 'idle' | 'syncing' | 'error' | 'disconnected';

export interface CloudSyncState {
  status: CloudSyncStatus;
  lastSyncTime: string | null;
  error: string | null;
  driveConnected: boolean;
}

type Listener = (state: CloudSyncState) => void;

class CloudSyncService {
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners: Set<Listener> = new Set();
  private state: CloudSyncState;
  private storageListener: ((e: StorageEvent) => void) | null = null;

  constructor() {
    this.state = {
      status: simplifiedDriveService.isConfigured() ? 'idle' : 'disconnected',
      lastSyncTime: localStorage.getItem(LAST_SYNC_KEY),
      error: null,
      driveConnected: simplifiedDriveService.isConfigured(),
    };
  }

  getState(): CloudSyncState {
    return { ...this.state };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    const snapshot = this.getState();
    this.listeners.forEach((fn) => fn(snapshot));
  }

  private setState(partial: Partial<CloudSyncState>) {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  /** Start watching for data changes and auto-syncing. */
  start() {
    if (!simplifiedDriveService.isConfigured()) {
      this.setState({ status: 'disconnected', driveConnected: false });
      return;
    }

    this.setState({ driveConnected: true, status: 'idle' });

    // Listen for cross-tab storage changes
    this.storageListener = (e: StorageEvent) => {
      if (e.key && WATCHED_KEYS.includes(e.key)) {
        this.scheduleSync();
      }
    };
    window.addEventListener('storage', this.storageListener);

    // Do an initial sync if data may have changed since last sync
    this.syncIfNeeded();
  }

  /** Stop watching and cancel pending syncs. */
  stop() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }
  }

  /** Call this when local data changes in the same tab. */
  notifyDataChanged() {
    if (this.state.driveConnected) {
      this.scheduleSync();
    }
  }

  private scheduleSync() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.performSync();
    }, SYNC_DEBOUNCE_MS);
  }

  private async syncIfNeeded() {
    // Compute a simple hash of current data to see if it changed since last sync
    const currentHash = this.computeDataHash();
    const lastHash = localStorage.getItem(DATA_HASH_KEY);

    if (currentHash !== lastHash) {
      await this.performSync();
    }
  }

  private computeDataHash(): string {
    let combined = '';
    for (const key of WATCHED_KEYS) {
      const val = localStorage.getItem(key);
      if (val) combined += val.length.toString();
    }
    // Simple fast hash — not cryptographic, just for change detection
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      hash = (hash * 31 + combined.charCodeAt(i)) | 0;
    }
    return hash.toString(36);
  }

  /** Perform a sync to Google Drive. */
  async performSync(): Promise<void> {
    if (!simplifiedDriveService.isConfigured()) {
      this.setState({ status: 'disconnected', driveConnected: false });
      return;
    }

    if (this.state.status === 'syncing') return;

    this.setState({ status: 'syncing', error: null });

    try {
      await dataBackupService.backupToGoogleDrive();
      const now = new Date().toISOString();
      localStorage.setItem(LAST_SYNC_KEY, now);
      localStorage.setItem(DATA_HASH_KEY, this.computeDataHash());
      this.setState({ status: 'idle', lastSyncTime: now, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      this.setState({ status: 'error', error: message });
    }
  }

  /** Restore from the most recent Google Drive backup. */
  async restoreFromDrive(): Promise<void> {
    if (!simplifiedDriveService.isConfigured()) {
      throw new Error('Google Drive not connected');
    }

    this.setState({ status: 'syncing', error: null });
    try {
      await dataBackupService.loadFromGoogleDrive();
      const now = new Date().toISOString();
      localStorage.setItem(LAST_SYNC_KEY, now);
      localStorage.setItem(DATA_HASH_KEY, this.computeDataHash());
      this.setState({ status: 'idle', lastSyncTime: now, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Restore failed';
      this.setState({ status: 'error', error: message });
      throw err;
    }
  }

  /** Check if there are backups available on Drive (for new-device restore flow). */
  async hasCloudBackups(): Promise<boolean> {
    if (!simplifiedDriveService.isConfigured()) return false;
    try {
      const files = await simplifiedDriveService.listBackupFiles();
      return files.length > 0;
    } catch {
      return false;
    }
  }

  /** Disconnect Google Drive and stop syncing. */
  disconnect() {
    this.stop();
    simplifiedDriveService.disconnect();
    localStorage.removeItem(LAST_SYNC_KEY);
    localStorage.removeItem(DATA_HASH_KEY);
    this.setState({
      status: 'disconnected',
      driveConnected: false,
      lastSyncTime: null,
      error: null,
    });
  }

  /** Re-check connection status (e.g. after OAuth callback). */
  refreshConnectionStatus() {
    const connected = simplifiedDriveService.isConfigured();
    this.setState({ driveConnected: connected, status: connected ? 'idle' : 'disconnected' });
    if (connected) {
      this.start();
    }
  }
}

export const cloudSyncService = new CloudSyncService();
